import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import {
  selectUserIsAppellant,
  selectUserIsAssessor,
  selectUserIsAuthorizedAttorney,
  selectUserIsSolicitor,
} from "./app";
import { RootState, AppDispatch } from ".";
import { RestCallStatus, RestStatus } from "../commontypes/common";
import pako from "pako";
import { selectAddOmitSeason, selectValueClassSeason } from "./admin";
import { Appeal, AppealFees, AppealRecord, AppealCategory, BatchAppeal, County, EntryMethod, HearingType, NewAppeal, PaymentMethod } from "../commontypes/types";

export type SaveSlot = {
  status: RestStatus;
  appealNumber?: string;
  error?: string;
};

interface NewAppealState {
  appeal: NewAppeal,
  batchAppealList: BatchAppeal[],
  existingAppeals: Appeal[],
  matchingAppealsForBLQ: AppealRecord[],
  appealsThatMayBeCrossed: AppealRecord[],
  flow?: AppealCategory,
  nextCounter?: number,
  feeRestStatus?: RestCallStatus,
  reservationRestStatus?: RestCallStatus,
  existingAppealsRestStatus?: RestCallStatus,
  saveSlots?: SaveSlot[],
  preferencesCopiedToContacts: boolean,
  createNewAddition: boolean,
}

export const initialState: NewAppealState = {
  appeal: {
    appealYear: 0,
    county: { key: '', label: '' },
    municipality: { key: '', label: '' },
    filingDescription: ' '
    ,
    property: {
      blq: '',
      address: '',
      cityState: '',
      zip: '',
      lotSize: '',
      classification: { key: '', label: '' },
      key: '',
      owner: '',
      relevantProperty: '',
      purchaseDate: '',
      purchasePrice: 0,
      tenData: null,
      currentAssessment: {
        total: 0,
        land: 0,
        buildingImprovement: 0,
        abatement: 0,
      },
    },
    valuation: {
      appealing: false,
      taxCourtPending: false,
    },
    classification: {
      appealing: false,
    },
    deduction: {
      veteranDeduction: false,
      disabledVeteranExemption: false,
      seniorCitizenOrDisabledPerson: false,
      abatement: false,
    },
    hearingDate: null,
    hearingLocation: null,
    entryMethod: EntryMethod.ONLINE,
    entryPaymentMethod: PaymentMethod.ONLINE_CARD,
    contact: {
      mainEmailCheckbox: false,
      mainPhoneCheckbox: false,
    },
    additionalContact: {
      notify: false,
    },
    thirdPartyContact: {
      notify: false,
    },
    authorizedAttorneyContact: {
      notify: false,
    },
    creditCardPayment: {
      paidAppeal: false
    },
    receiptId: '',
    fees: {
      propertyValuationFee: 0,
      deductionExemptionFee: 0,
      propertyClassificationFee: 0,
      convenienceFee: 0,
      total: 0,
    },
    crossAppeal: {
      isCrossAppeal: false,
    },
    notes: null,
    geolocation: {},
    deleted: false,
    tenRecords: null,
    improvementRecord: {},
    tenAppeal: false,
    appVersion: process.env.REACT_APP_VERSION || "0.0.0",
    creatorObj: {},
  },
  batchAppealList: [],
  existingAppeals: [],
  matchingAppealsForBLQ: [],
  appealsThatMayBeCrossed: [],
  preferencesCopiedToContacts: false,
  createNewAddition: false,
}


export const calculateFee = createAsyncThunk<
  AppealFees,
  void,
  { state: RootState; getState: () => RootState, dispatch: AppDispatch; requestId: string }
>(
  "appeal/calculateFee",
  async (_void, { getState }) => {
    const state = getState();
    const county = selectCounty(state);
    const userIsAssessor = selectUserIsAssessor(state);
    const userIsSolicitor = selectUserIsSolicitor(state);
    const userIsAuthorizedAttorney = selectUserIsAuthorizedAttorney(state);
    const userIsAppellant = selectUserIsAppellant(state);
    const filingDescription = selectFilingDescription(state);
    if (userIsAssessor || userIsSolicitor || userIsAuthorizedAttorney || (filingDescription === "A")) {
      return { propertyValuationFee: 0, deductionExemptionFee: 0, propertyClassificationFee: 0, convenienceFee: 0, total: 0 };
    }
    const response = await axios
      .get(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/fee`, {
        params: {
          currentAssessmentTotal: selectCurrentAssessmentTotal(state),
          currentAdditionAmount: selectCurrentAdditionAmount(state),
          appealingClassification: selectAppealingClassification(state),
          tenAppeal: selectIsTenAppeal(state),
          appealingValuation: selectAppealingValuation(state),
          appealingAbatementDenial: selectDeductionAbatement(state),
        },
      });
    const fees = response.data.fees;
    if (!userIsAppellant) {
      fees.total = fees.total - fees.convenienceFee;
      fees.convenienceFee = 0;
    }
    return fees;
  }
);

export const reserveAppealCounters = createAsyncThunk<
  { nextCounter: number },
  { count: number; appealYear: number },
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  "appeal/reserveAppealCounters",
  async ({ count, appealYear }, { getState }) => {
    const state = getState();
    const county = selectCounty(state);
    const municipality = selectMunicipality(state);
    const isTenAppeal = selectIsTenAppeal(state);
    const updateResponse = await axios
      .post(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/municipalities/${municipality.key}/appealcount`, {
        count: count,
        isTen: isTenAppeal,
        year: appealYear
      },
        {
        });
    return { nextCounter: updateResponse.data.firstCount };
  }
);

export type GetExistingAppealsForMunicipalitiesParams = {
  county: County;
  appealYear: number;
  isTen: boolean;
  municipalityIds: string[];
  filterForAuthAttorney?: boolean;
};
export const getExistingAppealsForMunicipalities = createAsyncThunk<
  Appeal[],
  GetExistingAppealsForMunicipalitiesParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  "appeal/getExistingAppeals",
  async ({ county, appealYear, isTen, municipalityIds, filterForAuthAttorney }, { getState }) => {
    const state = getState();
    const seasonConfiguration = isTen ? selectAddOmitSeason(state) : selectValueClassSeason(state);
    const judgmentSince = seasonConfiguration?.judgmentSinceFilter;
    const type = isTen ? 'ten' : 'twenty';
    const path = `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals?appealYear=${appealYear}&townId=${municipalityIds}&type=${type}&judgmentSince=${judgmentSince}`;
    const response = await axios.get(path);
    let data = JSON.parse(Buffer.from(pako.inflate(response.data.data)).toString()) as Appeal[];
    if (filterForAuthAttorney) {
      data = data.filter(obj => obj.APPEAL.authorizedAttorneyContact?.firstName);
    }
    return data;
  }
);

export type SaveAppealParams = {
  appealCounter?: number;
  saveSlotIndex: number;
  appeal: NewAppeal;
};

export const saveAppeal = createAsyncThunk<
  { appealNumber: string; saveSlotIndex: number },
  SaveAppealParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  "appeal/saveAppeal",
  async ({ appealCounter, saveSlotIndex, appeal }) => {
    const response = await axios
      .post(`${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${appeal.county.key}/appeals`, {
        appeal: appeal,
        count: appealCounter,
        appealYear: appeal.appealYear
      },
        {
        });
    return { appealNumber: response.data.appealNumber, saveSlotIndex };
  }
);

export const appealSlice = createSlice({
  name: 'newAppeal',
  initialState: initialState,
  reducers: {
    setFlow: (state, action) => {
      state.flow = action.payload;
    },

    clearFlow: (state) => {
      state.flow = undefined;
    },

    setCounty: (state, action) => {
      const { key, label } = action.payload;
      // const taxBoard = key ? countyTaxBoards[key] : {};
      // state.appeal.county = {key, label, ...taxBoard};
      state.appeal.county = { key, label };
    },

    setMunicipality: (state, action) => {
      state.appeal.municipality = action.payload;
    },

    unsetMunicipality: state => {
      state.appeal.municipality = initialState.appeal.municipality;
    },

    setProperty: (state, action) => {
      state.appeal.property = action.payload;
    },

    unsetProperty: state => {
      state.appeal.property = initialState.appeal.property;
    },

    unsetValuation: state => {
      state.appeal.valuation = initialState.appeal.valuation;
    },

    setValuation: (state, action) => {
      state.appeal.valuation = action.payload;
    },

    unsetFees: state => {
      state.appeal.fees = initialState.appeal.fees;
      state.feeRestStatus = { status: RestStatus.NOT_STARTED };
    },

    setBatchAppealList: (state, action) => {
      state.batchAppealList = action.payload;
    },

    unsetBatchAppealList: state => {
      state.batchAppealList = [];
    },

    setIsCrossAppeal: (state, action) => {
      state.appeal.crossAppeal.isCrossAppeal = action.payload;
    },

    setOtherAppeal: (state, action) => {
      state.appeal.crossAppeal.otherAppeal = action.payload;
    },

    unsetCrossAppeal: (state, action) => {
      state.appeal.crossAppeal = { isCrossAppeal: action.payload };
    },

    setIsTenAppeal: (state, action) => {
      state.appeal.tenAppeal = action.payload;
    },

    setAppealYear: (state, action) => {
      state.appeal.appealYear = action.payload;
    },

    setHearingType: (state, action) => {
      state.appeal.hearingType = action.payload;
    },

    setAppealingValuation: (state, action) => {
      state.appeal.valuation.appealing = action.payload;
    },

    setPurchasePrice: (state, action) => {
      state.appeal.valuation.purchasePrice = action.payload;
      state.appeal.property.purchasePrice = action.payload;
    },

    setPurchaseDate: (state, action) => {
      state.appeal.valuation.purchaseDate = action.payload;
      state.appeal.property.purchaseDate = action.payload;
    },

    setRequestedTotalAssessment: (state, action) => {
      state.appeal.valuation.requestedTotal = action.payload;
    },

    unsetRequestedTotalAssessment: state => {
      state.appeal.valuation.requestedTotal = undefined;
    },

    setTaxCourtPending: (state, action) => {
      state.appeal.valuation.taxCourtPending = action.payload;
    },

    unsetTaxCourtPending: state => {
      state.appeal.valuation.taxCourtPending = undefined;
    },

    setReasonForAppeal: (state, action) => {
      state.appeal.valuation.reason = action.payload;
    },

    unsetReasonForAppeal: state => {
      state.appeal.valuation.reason = undefined;
    },

    setAppealingClass: (state, action) => {
      state.appeal.classification.appealing = action.payload;
    },

    setRequestedClass: (state, action) => {
      state.appeal.classification.requestedClass = action.payload;
    },

    setCurrentClass: (state, action) => {
      state.appeal.classification.currentClass = action.payload;
    },

    unsetRequestedClass: state => {
      state.appeal.classification.requestedClass = undefined;
    },

    setVeteranDeduction: (state, action) => {
      state.appeal.deduction.veteranDeduction = action.payload;
    },

    setDisabledVeteranExemption: (state, action) => {
      state.appeal.deduction.disabledVeteranExemption = action.payload;
    },

    setSeniorCitizenOrDisabledPersonDeduction: (state, action) => {
      state.appeal.deduction.seniorCitizenOrDisabledPerson = action.payload;
    },

    setAbatementDeduction: (state, action) => {
      state.appeal.deduction.abatement = action.payload;
    },

    //Begin Contact
    setContactFirstName: (state, action) => {
      state.appeal.contact.firstName = action.payload;
    },
    setContactAddress: (state, action) => {
      state.appeal.contact.address = action.payload;
    },
    setContactCity: (state, action) => {
      state.appeal.contact.city = action.payload;
    },

    setContactZipCode: (state, action) => {
      state.appeal.contact.zipCode = action.payload;
    },
    setContactEmailAddress: (state, action) => {
      state.appeal.contact.emailAddress = action.payload;
    },
    setContactDayTimePhone: (state, action) => {
      state.appeal.contact.dayTimePhone = action.payload;
    },
    setMainEmailCheckbox: (state, action) => {
      state.appeal.contact.mainEmailCheckbox = action.payload;
    },
    setMainPhoneCheckbox: (state, action) => {
      state.appeal.contact.mainPhoneCheckbox = action.payload;
    },

    //Begin Additional Contact
    setNotifyAdditionalContact: (state, action) => {
      state.appeal.additionalContact.notify = action.payload;
    },
    setAdditionalContactFirstName: (state, action) => {
      state.appeal.additionalContact.firstName = action.payload;
    },
    setAdditionalContactAddress: (state, action) => {
      state.appeal.additionalContact.address = action.payload;
    },
    setAdditionalContactCity: (state, action) => {
      state.appeal.additionalContact.city = action.payload;
    },
    setAdditionalContactZipCode: (state, action) => {
      state.appeal.additionalContact.zipCode = action.payload;
    },
    setAdditionalContactEmailAddress: (state, action) => {
      state.appeal.additionalContact.emailAddress = action.payload;
    },
    setAdditionalContactDayTimePhone: (state, action) => {
      state.appeal.additionalContact.dayTimePhone = action.payload;
    },
    setACLawyerLicense: (state, action) => {
      state.appeal.additionalContact.aCLawyerLicense = action.payload;
    },
    setContactEmailCheckbox: (state, action) => {
      state.appeal.additionalContact.contactEmailCheckbox = action.payload;
    },
    setContactPhoneCheckbox: (state, action) => {
      state.appeal.additionalContact.contactPhoneCheckbox = action.payload;
    },
    setIsLawyer: (state, action) => {
      state.appeal.additionalContact.isLawyer = action.payload;
    },
    setIsAdditionalContact: (state, action) => {
      state.appeal.additionalContact.isAdditionalContact = action.payload;
    },
    unsetIsAdditionalContact: (state) => {
      state.appeal.additionalContact = {};
    },

    //Begin ThirdParty Contact
    setNotifyThirdPartyContact: (state, action) => {
      state.appeal.thirdPartyContact.notify = action.payload;
    },
    setThirdPartyContactFirstName: (state, action) => {
      state.appeal.thirdPartyContact.firstName = action.payload;
    },

    setThirdPartyContactAddress: (state, action) => {
      state.appeal.thirdPartyContact.address = action.payload;
    },
    setThirdPartyContactCity: (state, action) => {
      state.appeal.thirdPartyContact.city = action.payload;
    },

    setThirdPartyContactZipCode: (state, action) => {
      state.appeal.thirdPartyContact.zipCode = action.payload;
    },
    setThirdPartyContactEmailAddress: (state, action) => {
      state.appeal.thirdPartyContact.emailAddress = action.payload;
    },
    setThirdPartyContactDayTimePhone: (state, action) => {
      state.appeal.thirdPartyContact.dayTimePhone = action.payload;
    },
    setIsThirdPartyContact: (state, action) => {
      state.appeal.thirdPartyContact.isThirdPartyContact = action.payload;
    },
    unsetIsThirdPartyContact: (state) => {
      state.appeal.thirdPartyContact = {};
    },
    setThirdPartyEmailCheckbox: (state, action) => {
      state.appeal.thirdPartyContact.thirdPartyEmailCheckbox = action.payload;
    },
    setThirdPartyPhoneCheckbox: (state, action) => {
      state.appeal.thirdPartyContact.thirdPartyPhoneCheckbox = action.payload;
    },
    setAuthorizedAttorneyContactFirstName: (state, action) => {
      state.appeal.authorizedAttorneyContact.firstName = action.payload;
    },
    setAuthorizedAttorneyContactAddress: (state, action) => {
      state.appeal.authorizedAttorneyContact.address = action.payload;
    },
    setAuthorizedAttorneyContactCity: (state, action) => {
      state.appeal.authorizedAttorneyContact.city = action.payload;
    },
    setAuthorizedAttorneyContactZipCode: (state, action) => {
      state.appeal.authorizedAttorneyContact.zipCode = action.payload;
    },
    setAuthorizedAttorneyContactEmailAddress: (state, action) => {
      state.appeal.authorizedAttorneyContact.emailAddress = action.payload;
    },
    setAuthorizedAttorneyContactDayTimePhone: (state, action) => {
      state.appeal.authorizedAttorneyContact.dayTimePhone = action.payload;
    },
    setIsAuthorizedAttorneyContact: (state, action) => {
      state.appeal.authorizedAttorneyContact.isAuthorizedAttorneyContact = action.payload;
    },
    unsetIsAuthorizedAttorneyContact: (state) => {
      state.appeal.authorizedAttorneyContact = {};
    },
    setAuthorizedAttorneyEmailCheckbox: (state, action) => {
      state.appeal.authorizedAttorneyContact.authorizedAttorneyEmailCheckbox = action.payload;
    },
    setAuthorizedAttorneyPhoneCheckbox: (state, action) => {
      state.appeal.authorizedAttorneyContact.authorizedAttorneyPhoneCheckbox = action.payload;
    },
    setAALawyerLicense: (state, action) => {
      state.appeal.authorizedAttorneyContact.aALawyerLicense = action.payload;
    },

    // End

    setTenRecords: (state, action) => {
      state.appeal.tenRecords = action.payload;
    },

    setSelectedTen: (state, action) => {
      state.appeal.improvementRecord.originalRecord = action.payload;
      state.appeal.improvementRecord.listType = action.payload?.listType;
      state.appeal.improvementRecord.taxYear = action.payload?.taxYear;
    },
    setAdditionListType: (state, action) => {
      state.appeal.improvementRecord.listType = action.payload;
    },
    setAdditionTaxYear: (state, action) => {
      state.appeal.improvementRecord.taxYear = action.payload;
    },
    setAdditionPropertyClass: (state, action) => {
      state.appeal.improvementRecord.propertyClass = action.payload;
    },
    setRequestedAdditionDate: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionDate = action.payload;
    },

    setRequestedAdditionMonth: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionMonth = action.payload;
    },

    setRequestedAdditionAmount: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionAmount = action.payload;
    },


    setRequestedAdditionLandAmount: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionLand = action.payload;
    },


    setRequestedAdditionImprovementAmount: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionImprovement = action.payload;
    },


    setRequestedAdditionAbatementAmount: (state, action) => {
      state.appeal.improvementRecord.requestedAdditionAbatement = action.payload;
    },

    setEntryMethod: (state, action) => {
      state.appeal.entryMethod = action.payload;
    },
    setEntryPaymentMethod: (state, action) => {
      state.appeal.entryPaymentMethod = action.payload;
    },

    setFees: (state, action) => {
      state.appeal.fees = action.payload;
    },

    setReceiptId: (state, action) => {
      state.appeal.receiptId = action.payload;
    },

    setScanFile: (state, action) => {
      state.appeal.scanFile = action.payload;
    },

    setScanDenial: (state, action) => {
      state.appeal.scanDenial = action.payload;
    },

    setFilingDescription: (state, action) => {
      state.appeal.filingDescription = action.payload;
    },
    setIsBatchTemplate: (state, action) => {
      state.appeal.isBatchTemplate = action.payload;
    },

    // saveComparableSales: (state, data) => {
    //   state.appeal.comparableSales = action.payload;
    // },

    // setComparableSaleBlq: (list, data) => {
    //   return [
    //     ...list.slice(0, data.index),
    //     {
    //       ...listdata.index,
    //       blq: data.blq,
    //     },
    //     ...list.slice(data.index + 1),
    //   ]
    // },

    // setComparableSaleAddress: (list, data) => {
    //   return [
    //     ...list.slice(0, data.index),
    //     {
    //       ...listdata.index,
    //       address: data.address,
    //     },
    //     ...list.slice(data.index + 1),
    //   ]
    // },

    // setComparableSalePrice: (list, data) => {
    //   return [
    //     ...list.slice(0, data.index),
    //     {
    //       ...listdata.index,
    //       salePrice: data.salePrice,
    //     },
    //     ...list.slice(data.index + 1),
    //   ]
    // },

    // setComparableSaleDate: (list, data) => {
    //   return [
    //     ...list.slice(0, data.index),
    //     {
    //       ...listdata.index,
    //       saleDate: data.saleDate,
    //     },
    //     ...list.slice(data.index + 1),
    //   ]
    // },

    // removeComparableSale: (list, index) => {
    //   return ...list.slice(0, index), ...list.slice(index + 1)
    // },

    // addComparableSale: (state, key) => ({
    //   ...state,
    //   comparableSales: [
    //     ...state.comparableSales,
    //     {
    //       key: key,
    //       blq: '',
    //       address: '',
    //       salePrice: '',
    //       saleDate: '',
    //     },
    //   ],
    // }),

    // updateComparableSale: (state, data) => {
    //   const { key, row } = data
    //   return {
    //     ...state,
    //     comparableSales: state.appeal.comparableSales.map(comp => {
    //       if (comp.key !== key) {
    //         return comp
    //       }

    //       return {
    //         key: row.key,
    //         blq: row.blq,
    //         address: row.address,
    //         salePrice: row.salePrice,
    //         saleDate: row.saleDate,
    //       }
    //     }),
    //   }
    // },

    rememberExistingAppealsForBLQ: (state, action) => {
      state.matchingAppealsForBLQ = action.payload;
    },
    rememberAppealsThatMayBeCrossed: (state, action) => {
      state.appealsThatMayBeCrossed = action.payload;
    },
    clearAppeal: (state) => {
      state.appeal = initialState.appeal;
      state.matchingAppealsForBLQ = [];
      state.appealsThatMayBeCrossed = [];
      state.saveSlots = undefined;
      state.preferencesCopiedToContacts = false;
      state.createNewAddition = false;
    },
    prepareForSave: (state, action) => {
      const numberToSave = action.payload;
      state.saveSlots = Array(numberToSave);
    },
    setPreferencesCopiedToContacts: (state, action) => {
      state.preferencesCopiedToContacts = action.payload;
    },
    setCreateNewAddition: (state, action) => {
      state.createNewAddition = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(reserveAppealCounters.pending, (state) => {
        state.reservationRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(reserveAppealCounters.fulfilled, (state, action) => {
        const { nextCounter } = action.payload;
        state.reservationRestStatus = { status: RestStatus.SAVED };
        state.nextCounter = nextCounter;
      })
      .addCase(reserveAppealCounters.rejected, (state, action) => {
        state.reservationRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(saveAppeal.pending, (state, action) => {
        const { saveSlotIndex } = action.meta.arg;
        state.saveSlots![saveSlotIndex] = { status: RestStatus.SAVING };
      })
      .addCase(saveAppeal.fulfilled, (state, action) => {
        const { saveSlotIndex, appealNumber } = action.payload;
        state.saveSlots![saveSlotIndex] = { status: RestStatus.SAVED, appealNumber };
      })
      .addCase(saveAppeal.rejected, (state, action) => {
        const { saveSlotIndex } = action.meta.arg;
        state.saveSlots![saveSlotIndex] = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(calculateFee.pending, (state) => {
        state.feeRestStatus = { status: RestStatus.LOADING };
      })
      .addCase(calculateFee.fulfilled, (state, action) => {
        state.feeRestStatus = { status: RestStatus.LOADED };
        state.appeal.fees = action.payload;
        if (action.payload.total === 0) {
          state.appeal.entryPaymentMethod = PaymentMethod.NONE;
        }
      })
      .addCase(calculateFee.rejected, (state, action) => {
        state.feeRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
      .addCase(getExistingAppealsForMunicipalities.pending, (state) => {
        state.existingAppealsRestStatus = { status: RestStatus.LOADING };
        state.existingAppeals = [];
      })
      .addCase(getExistingAppealsForMunicipalities.fulfilled, (state, action) => {
        state.existingAppealsRestStatus = { status: RestStatus.LOADED };
        state.existingAppeals = action.payload;
      })
      .addCase(getExistingAppealsForMunicipalities.rejected, (state, action) => {
        state.existingAppealsRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
  }
});
export const { setCounty, setMunicipality, unsetMunicipality, setProperty, unsetProperty, setBatchAppealList, unsetBatchAppealList, setIsCrossAppeal, setOtherAppeal, unsetCrossAppeal,
  setAppealingValuation, setPurchasePrice,
  setPurchaseDate, setRequestedTotalAssessment, unsetRequestedTotalAssessment, setTaxCourtPending, unsetTaxCourtPending, setReasonForAppeal,
  unsetReasonForAppeal, setAppealingClass, setCurrentClass, setRequestedClass, unsetRequestedClass, setVeteranDeduction, setDisabledVeteranExemption,
  setSeniorCitizenOrDisabledPersonDeduction, setAbatementDeduction, setContactFirstName, setContactAddress, setContactCity,
  setContactZipCode, setContactEmailAddress, setContactDayTimePhone, setMainEmailCheckbox, setMainPhoneCheckbox,
  setNotifyAdditionalContact, setAdditionalContactFirstName, setAdditionalContactAddress, setAdditionalContactCity,
  setAdditionalContactZipCode, setAdditionalContactEmailAddress, setAdditionalContactDayTimePhone,
  setACLawyerLicense, setContactEmailCheckbox, setContactPhoneCheckbox, setIsLawyer, setIsAdditionalContact, unsetIsAdditionalContact,
  setNotifyThirdPartyContact, setThirdPartyContactFirstName, setThirdPartyContactAddress, setThirdPartyContactCity,
  setThirdPartyContactZipCode, setThirdPartyEmailCheckbox, setThirdPartyPhoneCheckbox, setThirdPartyContactEmailAddress,
  setThirdPartyContactDayTimePhone, setIsThirdPartyContact, unsetIsThirdPartyContact, setAALawyerLicense,
  setAuthorizedAttorneyContactFirstName, setAuthorizedAttorneyContactAddress,
  setAuthorizedAttorneyContactCity, setAuthorizedAttorneyContactZipCode, setAuthorizedAttorneyContactEmailAddress,
  setAuthorizedAttorneyContactDayTimePhone, setAuthorizedAttorneyEmailCheckbox, setAuthorizedAttorneyPhoneCheckbox,
  setIsAuthorizedAttorneyContact, unsetIsAuthorizedAttorneyContact,
  setEntryMethod, setEntryPaymentMethod, setReceiptId, setScanFile, setScanDenial, setFilingDescription, setFees,
  setIsTenAppeal, setTenRecords, setSelectedTen, setRequestedAdditionDate, setRequestedAdditionAmount, setRequestedAdditionLandAmount,
  setRequestedAdditionImprovementAmount, setRequestedAdditionAbatementAmount, setRequestedAdditionMonth, setHearingType,
  clearAppeal, setAppealYear, setIsBatchTemplate, prepareForSave, setFlow, clearFlow, setValuation, unsetValuation, unsetFees,
  setAdditionListType, setAdditionTaxYear, rememberExistingAppealsForBLQ, rememberAppealsThatMayBeCrossed, setAdditionPropertyClass,
  setPreferencesCopiedToContacts, setCreateNewAddition } = appealSlice.actions;

export const selectFlow = (state: RootState) => state.newAppeal.flow;
export const selectAppealYear = (state: RootState) => state.newAppeal.appeal.appealYear;
export const selectCounty = (state: RootState) => state.newAppeal.appeal.county;
export const selectMunicipality = (state: RootState) => state.newAppeal.appeal.municipality;
export const selectIsTenAppeal = (state: RootState) => state.newAppeal.appeal.tenAppeal;
export const selectTenRecords = (state: RootState) => state.newAppeal.appeal.tenRecords;
export const selectAppeal = (state: RootState) => state.newAppeal.appeal;
export const selectCurrentAssessmentLand = (state: RootState) => state.newAppeal.appeal.property.currentAssessment.land;
export const selectCurrentAssessmentBuildingImprovement = (state: RootState) => state.newAppeal.appeal.property.currentAssessment.buildingImprovement;
export const selectCurrentAssessmentAbatement = (state: RootState) => state.newAppeal.appeal.property.currentAssessment.abatement;
export const selectCurrentAssessmentTotal = (state: RootState) => state.newAppeal.appeal.property.currentAssessment.total;
export const selectCurrentAdditionAmount = (state: RootState) => {
  const originalRecord = state.newAppeal.appeal.improvementRecord.originalRecord;
  return originalRecord
    ? (originalRecord.additionImprovement + originalRecord.additionLand - originalRecord.additionAbatement) * (Number(originalRecord.totalMonthsAssessed) / 12)
    : 0;
};
export const selectAssessmentAddition = (state: RootState) => state.newAppeal.appeal.improvementRecord.originalRecord;
export const selectAdditionListType = (state: RootState) => state.newAppeal.appeal.improvementRecord.listType;
export const selectAdditionTaxYear = (state: RootState) => state.newAppeal.appeal.improvementRecord.taxYear;
export const selectAdditionPropertyClass = (state: RootState) => state.newAppeal.appeal.improvementRecord.propertyClass;
export const selectRequestedAdditionLand = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionLand;
export const selectRequestedAdditionImprovement = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionImprovement;
export const selectRequestedAdditionAbatement = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionAbatement;
export const selectRequestedAdditionAmount = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionAmount;
export const selectRequestedAdditionDate = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionDate;
export const selectRequestedAdditionMonth = (state: RootState) => state.newAppeal.appeal.improvementRecord.requestedAdditionMonth;
export const selectRequestedPropertyClassification = (state: RootState) => state.newAppeal.appeal.classification?.requestedClass;
export const selectAppealingClassification = (state: RootState) => state.newAppeal.appeal.classification.appealing;
export const selectAppealingValuation = (state: RootState) => state.newAppeal.appeal.valuation.appealing;
export const selectRequestedTotal = (state: RootState) => state.newAppeal.appeal.valuation.requestedTotal;
export const selectReasonForAppeal = (state: RootState) => state.newAppeal.appeal.valuation.reason;
export const selectTaxCourtPending = (state: RootState) => state.newAppeal.appeal.valuation.taxCourtPending;
export const selectPurchasePrice = (state: RootState) => state.newAppeal.appeal.valuation.purchasePrice;
export const selectPurchaseDate = (state: RootState) => state.newAppeal.appeal.valuation.purchaseDate;
export const selectProperty = (state: RootState) => state.newAppeal.appeal.property;
export const selectPropertyClassification = (state: RootState) => state.newAppeal.appeal.property.classification;
export const selectAdditionalContactIsLawyer = (state: RootState) => state.newAppeal.appeal.additionalContact?.isLawyer;
export const selectContactName = (state: RootState) => state.newAppeal.appeal.contact.firstName;
export const selectContactEmailAddress = (state: RootState) => state.newAppeal.appeal.contact.emailAddress;
export const selectContactDayTimePhone = (state: RootState) => state.newAppeal.appeal.contact.dayTimePhone;
export const selectAdditionalContactName = (state: RootState) => state.newAppeal.appeal.additionalContact.firstName;
export const selectAdditionalContactEmailAddress = (state: RootState) => state.newAppeal.appeal.additionalContact.emailAddress;
export const selectAdditionalContactDayTimePhone = (state: RootState) => state.newAppeal.appeal.additionalContact.dayTimePhone;
export const selectHasAdditionalContact = (state: RootState) => state.newAppeal.appeal.additionalContact.isAdditionalContact;
export const selectHasThirdPartyContact = (state: RootState) => state.newAppeal.appeal.thirdPartyContact?.isThirdPartyContact;
export const selectHasAuthorizedAttorneyContact = (state: RootState) => !!state.newAppeal.appeal.authorizedAttorneyContact;
export const selectContact = (state: RootState) => state.newAppeal.appeal.contact;
export const selectAdditionalContact = (state: RootState) => state.newAppeal.appeal.additionalContact;
export const selectThirdPartyContact = (state: RootState) => state.newAppeal.appeal.thirdPartyContact;
export const selectAuthorizedAttorneyContact = (state: RootState) => state.newAppeal.appeal.authorizedAttorneyContact;
export const selectSaveSlots = (state: RootState) => state.newAppeal.saveSlots;
export const selectFilingDescription = (state: RootState) => state.newAppeal.appeal.filingDescription;

export const selectBatchAppealList = (state: RootState) => state.newAppeal.batchAppealList;
export const selectExistingAppeals = (state: RootState) => state.newAppeal.existingAppeals;
export const selectAppealsThatMayBeCrossed = (state: RootState) => state.newAppeal.appealsThatMayBeCrossed;
export const selectExistingAppealsForBLQ = (state: RootState) => state.newAppeal.matchingAppealsForBLQ;
export const selectExistingAppealsRestStatus = (state: RootState) => state.newAppeal.existingAppealsRestStatus;
export const selectExistingAppealsError = (state: RootState) => state.newAppeal.existingAppealsRestStatus?.error;
export const selectFees = (state: RootState) => state.newAppeal.appeal.fees;
export const selectFeeLoaded = (state: RootState) => state.newAppeal.feeRestStatus?.status === RestStatus.LOADED;
export const selectDeductionVeteranDeduction = (state: RootState) => state.newAppeal.appeal.deduction.veteranDeduction;
export const selectDeductionDisabledVeteranExemption = (state: RootState) => state.newAppeal.appeal.deduction.disabledVeteranExemption;
export const selectDeductionSeniorCitizenOrDisabledPerson = (state: RootState) => state.newAppeal.appeal.deduction.seniorCitizenOrDisabledPerson;
export const selectDeductionAbatement = (state: RootState) => state.newAppeal.appeal.deduction.abatement;
export const selectEntryPaymentMethod = (state: RootState) => state.newAppeal.appeal.entryPaymentMethod;
export const selectReceiptId = (state: RootState) => state.newAppeal.appeal.receiptId;
export const selectEntryMethod = (state: RootState) => state.newAppeal.appeal.entryMethod;
export const selectHearingType = (state: RootState) => state.newAppeal.appeal.hearingType;
export const selectScanDenial = (state: RootState) => state.newAppeal.appeal.scanDenial;
export const selectPreferencesCopiedToContacts = (state: RootState) => state.newAppeal.preferencesCopiedToContacts;
export const selectCreateNewAddition = (state: RootState) => state.newAppeal.createNewAddition;
export default appealSlice.reducer;
